import type { MechanicDefinition } from "./types";

export interface CustomRule {
  title: string;
  text: string;
  targets: string[];
  enabled: boolean;
}

export const INJECTION_TARGETS = [
  { value: "narrator", label: "Narrator", description: "Main narrator system prompt" },
  { value: "npc_agent", label: "NPC Agent", description: "Character agent dialogue context" },
  { value: "director", label: "Director", description: "Pre-narration director notes" },
  { value: "gm_chat", label: "GM Chat", description: "GM Chat out-of-band responses" },
  { value: "world_builder", label: "World Builder", description: "World-builder agent context" },
  { value: "skill_checks", label: "Skill Checks", description: "Skill check resolution context" },
  { value: "combat", label: "Combat", description: "Combat resolution context" },
  { value: "dialogue", label: "Dialogue", description: "Dialogue system context" },
] as const;

export type InjectionTarget = typeof INJECTION_TARGETS[number]["value"];

/** Helper: filter rules by target, returning only enabled ones */
export function getRulesByTarget(config: Record<string, any>, target: InjectionTarget): CustomRule[] {
  const rules: CustomRule[] = config.rules || [];
  return rules.filter((r) => r.enabled && r.targets.includes(target));
}

/** Format filtered rules into a prompt block */
export function formatRulesBlock(rules: CustomRule[], heading = "Custom Rules"): string {
  if (rules.length === 0) return "";
  return `## ${heading}\n${rules.map((r, i) => `${i + 1}. **${r.title}**: ${r.text}`).join("\n")}`;
}

export const customRules: MechanicDefinition = {
  id: "custom_rules",
  name: "Custom Rules",
  description: "Define freeform rules injected into specific AI agents. Use for language barriers, cultural norms, NPC behavior defaults, or any custom world logic.",
  icon: "ScrollText",
  category: "systems",
  aiHints: {
    purpose: "Injects freeform text rules into specific AI agents (narrator, NPC, combat, etc.) for custom world logic.",
    whenToUse: "When the builder needs rules that don't fit any existing mechanic — language barriers, cultural norms, custom physics.",
    synergies: [],
    conflicts: [],
    prerequisites: [],
    tags: ["custom", "freeform", "rules", "injection", "flexible"],
    complexity: 1,
    auditPasses: 1,
    exampleConfig: {},
    stateVariables: [],
  },
  configSchema: [], // Uses custom editor (CustomRulesEditor)
  systemPromptSection: (config) => {
    const narratorRules = getRulesByTarget(config, "narrator");
    return formatRulesBlock(narratorRules);
  },
  tools: [],
};
